import { useEffect, useMemo, useState } from 'react';
import {
  addSectionComment,
  ContentReviewInterface,
  ReviewComment,
  updateSectionChange,
} from '../../models/ContentReviewInterface';
import { useReviewCommentFlows } from './useReviewCommentFlows';
import {
  generateImageCarouselType,
  generateLeftRightListType,
  generateStandardHeaderTextType,
  generateTableType,
} from '../../services/TemplateUtils';
import { ReviewContentEditViewProps, ReviewContentType } from './reviewContentEditViewTypes';
import {
  buildCommentOverlays,
  computeHasUnsavedChanges,
  createAddContentItem,
  getContentTypeDisplayName,
} from './reviewContentEditViewUtils';

export function useReviewContentEditView({
  publishedContent,
  draftContent,
  reviewData,
  onReviewDataUpdate,
  currentUser,
  modelType = 'FundOpportunity',
}: ReviewContentEditViewProps) {
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<any>(null);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAddContentDialog, setShowAddContentDialog] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState<ReviewContentType | null>(null);
  const [selectedInsertIndex, setSelectedInsertIndex] = useState<number | null>(null);
  const [showReplaceComponentDialog, setShowReplaceComponentDialog] = useState(false);
  const [selectedReplaceContentType, setSelectedReplaceContentType] =
    useState<ReviewContentType | null>(null);
  const [selectedReplaceSectionId, setSelectedReplaceSectionId] = useState<string | null>(null);
  const [previousComponent, setPreviousComponent] = useState<any>(null);
  const [showPreviousComponentDialog, setShowPreviousComponentDialog] = useState(false);
  const [showOriginalComponentDialog, setShowOriginalComponentDialog] = useState(false);
  const [originalComponentContent, setOriginalComponentContent] = useState<any>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const {
    showAddCommentDialog,
    addCommentText,
    showMinimalReply,
    minimalReplyText,
    setShowMinimalReply,
    setMinimalReplyText,
    setAddCommentText,
    handleAddCommentSubmit,
    handleAddCommentCancel,
    handleMinimalReplySubmit,
    handleMinimalReplyCancel,
  } = useReviewCommentFlows({ reviewData, onReviewDataUpdate, currentUser, setSubmitting });

  const commentOverlays = useMemo(
    () => buildCommentOverlays(reviewData.comments),
    [reviewData.comments],
  );

  useEffect(() => {
    setHasUnsavedChanges(computeHasUnsavedChanges(reviewData, editingSectionId, editedContent));
  }, [editingSectionId, editedContent, reviewData]);

  const handleEditStart = (sectionId: string, originalContent: any) => {
    setEditingSectionId(sectionId);
    const existingChange = reviewData.changes.find((c) => c.sectionId === sectionId);
    setEditedContent(existingChange?.changed ? existingChange.contentAfter : originalContent);
  };

  const handleEditCancel = () => {
    setEditingSectionId(null);
    setEditedContent(null);
  };

  const handleRevertToDraft = async (sectionId: string) => {
    setSubmitting(true);
    try {
      const updatedReviewData = {
        ...reviewData,
        changes: reviewData.changes.filter((change) => change.sectionId !== sectionId),
      };
      await onReviewDataUpdate(updatedReviewData);
    } catch (error) {
      console.error('Error reverting to draft:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSave = (_sectionId: string, originalContent: any) => {
    const contentChanged = JSON.stringify(editedContent) !== JSON.stringify(originalContent);
    if (contentChanged) {
      setShowCommentDialog(true);
    } else {
      setEditingSectionId(null);
      setEditedContent(null);
    }
  };

  const handleCommentSubmit = async () => {
    if (!comment.trim() || !editingSectionId || !editedContent) {
      return;
    }

    setSubmitting(true);
    try {
      const sectionIndex = parseInt(editingSectionId.split('-')[1], 10);
      const originalContent = draftContent.content[sectionIndex];

      const newConversation = {
        conversationId: `conversation-${Date.now()}`,
        sectionId: editingSectionId,
        comments: [
          {
            message: comment,
            submittedBy: { id: currentUser.id, name: currentUser.name },
            createdAt: new Date().toISOString(),
          } as ReviewComment,
        ],
      };

      const changeProposal = {
        sectionId: editingSectionId,
        sectionType: editedContent.type,
        contentBefore: originalContent,
        contentAfter: editedContent,
        commentId: newConversation.conversationId,
        changed: true,
      };

      let updatedReviewData = updateSectionChange(reviewData, changeProposal);
      updatedReviewData = addSectionComment(updatedReviewData, newConversation);
      await onReviewDataUpdate(updatedReviewData);

      setEditingSectionId(null);
      setEditedContent(null);
      setShowCommentDialog(false);
      setComment('');
    } catch (error) {
      console.error('Error submitting edit:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentCancel = () => {
    setShowCommentDialog(false);
    setComment('');
  };

  const handleAddContent = (insertIndex: number) => {
    setSelectedInsertIndex(insertIndex);
    setShowAddContentDialog(true);
  };

  const handleAddContentSubmit = async () => {
    if (!selectedContentType || selectedInsertIndex === null) {
      return;
    }

    setSubmitting(true);
    try {
      const newContentItem = createAddContentItem(selectedContentType);
      const sectionId = `section-${selectedInsertIndex}`;

      const newConversation = {
        conversationId: `conversation-${Date.now()}`,
        sectionId,
        comments: [
          {
            message: `Added new ${selectedContentType} content item`,
            submittedBy: { id: currentUser.id, name: currentUser.name },
            createdAt: new Date().toISOString(),
          } as ReviewComment,
        ],
      };

      const changeProposal = {
        sectionId,
        sectionType: selectedContentType,
        contentBefore: null,
        contentAfter: newContentItem,
        commentId: newConversation.conversationId,
        changed: true,
      };

      let updatedReviewData = updateSectionChange(reviewData, changeProposal);
      updatedReviewData = addSectionComment(updatedReviewData, newConversation);
      await onReviewDataUpdate(updatedReviewData);

      setShowAddContentDialog(false);
      setSelectedContentType(null);
      setSelectedInsertIndex(null);
      setEditingSectionId(sectionId);
      setEditedContent(newContentItem);
    } catch (error) {
      console.error('Error adding content:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddContentCancel = () => {
    setShowAddContentDialog(false);
    setSelectedContentType(null);
    setSelectedInsertIndex(null);
  };

  const handleReplaceComponent = (sectionId: string, currentContent: any) => {
    setSelectedReplaceSectionId(sectionId);
    setPreviousComponent(currentContent);
    setShowReplaceComponentDialog(true);
  };

  const createReplacementContentItem = (contentType: ReviewContentType) => {
    switch (contentType) {
      case 'StandardHeaderText':
        return generateStandardHeaderTextType();
      case 'LeftRightList':
        return generateLeftRightListType();
      case 'Table':
        return generateTableType();
      case 'ImageCarousel':
        return generateImageCarouselType(
          publishedContent.relatedObjectId || '',
          modelType || 'FundOpportunity',
        );
      default:
        return null;
    }
  };

  const handleReplaceComponentSubmit = async () => {
    if (!selectedReplaceContentType || !selectedReplaceSectionId || !previousComponent) {
      return;
    }

    setSubmitting(true);
    try {
      const newContentItem = createReplacementContentItem(selectedReplaceContentType);
      if (!newContentItem) {
        return;
      }

      const newConversation = {
        conversationId: `conversation-${Date.now()}`,
        sectionId: selectedReplaceSectionId,
        comments: [
          {
            message: `Replaced ${getContentTypeDisplayName(previousComponent.type)} with ${getContentTypeDisplayName(selectedReplaceContentType)}`,
            submittedBy: { id: 'system', name: 'System' },
            createdAt: new Date().toISOString(),
          } as ReviewComment,
        ],
      };

      const changeProposal = {
        sectionId: selectedReplaceSectionId,
        sectionType: selectedReplaceContentType,
        contentBefore: previousComponent,
        contentAfter: newContentItem,
        commentId: newConversation.conversationId,
        changed: true,
      };

      let updatedReviewData = updateSectionChange(reviewData, changeProposal);
      updatedReviewData = addSectionComment(updatedReviewData, newConversation);
      await onReviewDataUpdate(updatedReviewData);

      setShowReplaceComponentDialog(false);
      setSelectedReplaceContentType(null);
      setSelectedReplaceSectionId(null);
      setPreviousComponent(null);
      setEditingSectionId(selectedReplaceSectionId);
      setEditedContent(newContentItem);
    } catch (error) {
      console.error('Error replacing component:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplaceComponentCancel = () => {
    setShowReplaceComponentDialog(false);
    setSelectedReplaceContentType(null);
    setSelectedReplaceSectionId(null);
    setPreviousComponent(null);
  };

  const handleViewOriginalComponent = (sectionId: string) => {
    const sectionIndex = parseInt(sectionId.replace('section-', ''), 10);
    if (publishedContent.content && publishedContent.content[sectionIndex]) {
      setOriginalComponentContent(publishedContent.content[sectionIndex]);
      setShowOriginalComponentDialog(true);
    }
  };

  const handleSaveAllChangesToDraft = async () => {
    setSubmitting(true);
    try {
      const updatedReviewData = { ...reviewData };
      updatedReviewData.changes = updatedReviewData.changes.map((change) => ({
        ...change,
        accepted: true,
        acceptedBy: { id: currentUser.id, name: currentUser.name || 'Unknown' },
        acceptedAt: new Date().toISOString(),
      }));
      await onReviewDataUpdate(updatedReviewData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving all changes to draft:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    modelType,
    editingSectionId,
    editedContent,
    submitting,
    hasUnsavedChanges,
    commentOverlays,
    showMinimalReply,
    minimalReplyText,
    dialogState: {
      showCommentDialog,
      comment,
      showAddCommentDialog,
      addCommentText,
      showAddContentDialog,
      selectedContentType,
      showReplaceComponentDialog,
      selectedReplaceContentType,
      previousComponent,
      showPreviousComponentDialog,
      showOriginalComponentDialog,
      originalComponentContent,
    },
    setEditedContent,
    setShowMinimalReply,
    setMinimalReplyText,
    setComment,
    setAddCommentText,
    setSelectedContentType,
    setSelectedReplaceContentType,
    handleEditStart,
    handleEditCancel,
    handleEditSave,
    handleRevertToDraft,
    handleCommentSubmit,
    handleCommentCancel,
    handleAddCommentSubmit,
    handleAddCommentCancel,
    handleAddContent,
    handleAddContentSubmit,
    handleAddContentCancel,
    handleReplaceComponent,
    handleReplaceComponentSubmit,
    handleReplaceComponentCancel,
    handleViewPreviousComponent: () => setShowPreviousComponentDialog(true),
    handleClosePreviousComponent: () => setShowPreviousComponentDialog(false),
    handleViewOriginalComponent,
    handleCloseOriginalComponent: () => {
      setShowOriginalComponentDialog(false);
      setOriginalComponentContent(null);
    },
    handleSaveAllChangesToDraft,
    handleMinimalReplySubmit,
    handleMinimalReplyCancel,
  };
}

export type ReviewContentEditViewController = ReturnType<typeof useReviewContentEditView>;
